/**
 * Tandem — the visit pool: work owed but not yet in a diary, over a rolling
 * due-date horizon. Keyed on DUE date, not visit start, which is exactly why the
 * schedule cannot see this work; the two screens are complementary.
 *
 * It deliberately proposes no pairings. The spec is explicit that postcode area
 * was a stand-in and the real rule is drive distance, so half-day work is only
 * labelled pairable-on-capacity until routing exists.
 */
import { useMemo, useState } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { T } from "./tokens";
import { Panel } from "./ui";
import { useVisitPool, toISODate, addDays, useTeamFilter } from "./data";
import type { SfVisit } from "./data";
import { scoreVisit, fmtPoints, VISIT_TYPE_LABEL, classify } from "./points";
import { usePointsConfig } from "./pointsConfig";

/** Horizon presets. 90 days is the spec's pairing window, so it leads. */
const HORIZONS = [
    { days: 30, label: "30 days" },
    { days: 90, label: "90 days" },
    { days: 180, label: "6 months" },
];

const BUCKETS = [
    { key: "overdue", label: "Overdue", tone: "red" },
    { key: "soon", label: "Next 14 days", tone: "amber" },
    { key: "later", label: "Beyond 14 days", tone: "plain" },
] as const;

const daysBetween = (a: Date, b: Date) => Math.round((b.getTime() - a.getTime()) / 86400000);

const Metric = ({ label, value, tip, color }: { label: string; value: string | number; tip?: string; color?: string }) => (
    <Box title={tip} sx={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: "12px", padding: "12px 14px" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "7px" }}>
            <Box sx={{ width: "8px", height: "8px", borderRadius: "50%", background: color || T.purple, flexShrink: 0 }} />
            <Typography sx={{ fontSize: "10px", fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: T.muted }}>
                {label}
            </Typography>
        </Box>
        <Typography sx={{ fontSize: "22px", fontWeight: 800, color: T.ink2, mt: "4px", fontVariantNumeric: "tabular-nums" }}>
            {value}
        </Typography>
    </Box>
);

const GRID = "92px 1.6fr 1.3fr 78px 1fr 92px 84px";

const PoolScreen = ({ team = "AllTeams" }: { team?: string }) => {
    const { config } = usePointsConfig();
    const inTeam = useTeamFilter(team);
    const [horizon, setHorizon] = useState(90);
    const [includeOverdue, setIncludeOverdue] = useState(true);

    const today = new Date();
    // Rolling window. Overdue work is real and owed, so it is pulled in — but a
    // 12-month floor, because the historic backlog is large.
    const from = includeOverdue ? addDays(today, -365) : today;
    const to = addDays(today, horizon);
    const pool = useVisitPool(toISODate(from), toISODate(to));

    const rows = useMemo(() => {
        const all = pool.data ?? [];
        return all
            .filter((v) => inTeam(v.allocatedToEmail))
            .map((v) => {
                const score = scoreVisit(
                    {
                        subActivity: v.subActivity,
                        name: v.name,
                        visitCategory: v.visitCategory,
                        industry: v.industry,
                        fraRiskLevel: v.fraRiskLevel,
                        highRiskFra: v.highRiskFra,
                    },
                    config,
                    // Unbooked work has no On Site / Remote flag yet.
                    { assumeOnSiteIfUnset: true },
                );
                const due = v.dueDate ? new Date(v.dueDate) : null;
                const overdueBy = due ? daysBetween(due, today) : 0;
                const bucket = !due ? "later" : overdueBy > 0 ? "overdue" : overdueBy > -15 ? "soon" : "later";
                return { v, score, due, overdueBy, bucket };
            })
            .sort((a, b) => (a.v.dueDate || "").localeCompare(b.v.dueDate || ""));
    }, [pool.data, inTeam, config, today]);

    const totals = useMemo(() => {
        const points = rows.reduce((n, r) => n + (r.score.points ?? 0), 0);
        return {
            count: rows.length,
            points: Math.round(points * 10) / 10,
            // Half-day work is what pairing would consume. Counted, not paired.
            pairable: rows.filter((r) => r.score.points === config.pairableCapacity).length,
            unrated: rows.filter((r) => r.score.industryMissing).length,
            overdue: rows.filter((r) => r.bucket === "overdue").length,
        };
    }, [rows, config]);

    if (pool.isLoading)
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: "48px" }}>
                <CircularProgress size={26} />
            </Box>
        );
    if (pool.isError) return <Box sx={{ p: "28px", color: T.muted }}>Couldn't load the visit pool.</Box>;

    return (
        <Box sx={{ padding: "24px 28px 56px", display: "flex", flexDirection: "column", gap: "16px" }}>
            {/* Horizon — the pool is a moving window, so make moving it the first control */}
            <Box sx={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                <Typography sx={{ fontSize: "12.5px", fontWeight: 700, color: T.muted }}>Due within</Typography>
                <Box sx={{ display: "flex", border: `1px solid ${T.border}`, borderRadius: "8px", overflow: "hidden" }}>
                    {HORIZONS.map((h) => (
                        <Box
                            key={h.days}
                            component="button"
                            onClick={() => setHorizon(h.days)}
                            sx={{
                                border: "none",
                                background: horizon === h.days ? T.purple : T.card,
                                color: horizon === h.days ? "#fff" : T.muted,
                                padding: "6px 14px",
                                fontSize: "12.5px",
                                fontWeight: 700,
                                cursor: "pointer",
                                fontFamily: T.font,
                            }}
                        >
                            {h.label}
                        </Box>
                    ))}
                </Box>
                <Box
                    component="button"
                    onClick={() => setIncludeOverdue((o) => !o)}
                    sx={{
                        border: `1px solid ${includeOverdue ? T.red : T.border}`,
                        background: includeOverdue ? "#FBF1EF" : T.card,
                        color: includeOverdue ? T.red : T.muted,
                        borderRadius: "999px",
                        padding: "6px 13px",
                        fontSize: "12px",
                        fontWeight: 700,
                        cursor: "pointer",
                        fontFamily: T.font,
                    }}
                >
                    {includeOverdue ? "✓ " : ""}Include overdue
                </Box>
                <Typography sx={{ fontSize: "11.5px", color: T.muted2, ml: "auto" }}>
                    {toISODate(from)} → {toISODate(to)}
                </Typography>
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "12px" }}>
                <Metric label="Visits owed" value={totals.count} tip="Work with a due date in this window and no diary slot" />
                <Metric label="Points owed" value={fmtPoints(totals.points)} tip="What this work would score once delivered" color={T.purpleDeep} />
                <Metric label="Overdue" value={totals.overdue} tip="Past its due date and still not booked" color={T.red} />
                <Metric
                    label="Half-day work"
                    value={totals.pairable}
                    tip="Scores 1 point, so two could share a day — once drive times exist to say which two"
                    color={T.amber}
                />
                <Metric label="No industry" value={totals.unrated} tip="Scored on the default; the account needs its industry set" color={T.amber} />
            </Box>

            {BUCKETS.map((b) => {
                const inBucket = rows.filter((r) => r.bucket === b.key);
                if (inBucket.length === 0) return null;
                const pts = Math.round(inBucket.reduce((n, r) => n + (r.score.points ?? 0), 0) * 10) / 10;
                return (
                    <Panel
                        key={b.key}
                        title={`${b.label} · ${inBucket.length}`}
                        sub={`${fmtPoints(pts)} points of work`}
                    >
                        <Box sx={{ overflowX: "auto" }}>
                            <Box sx={{ minWidth: "900px" }}>
                                <Box sx={{ display: "grid", gridTemplateColumns: GRID, background: T.rowTint, borderRadius: "8px", padding: "7px 10px" }}>
                                    {["Due", "Account", "Visit type", "Points", "Consultant", "Status"].map((h) => (
                                        <Typography key={h} sx={{ fontSize: "10px", fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: T.muted }}>
                                            {h}
                                        </Typography>
                                    ))}
                                </Box>
                                {inBucket.slice(0, 200).map(({ v, score, due, overdueBy }) => (
                                    <Box key={v.id} sx={{ display: "grid", gridTemplateColumns: GRID, alignItems: "center", padding: "7px 10px", borderTop: `1px solid ${T.borderSoft}` }}>
                                        <Typography
                                            title={overdueBy > 0 ? `${overdueBy} days overdue` : undefined}
                                            sx={{ fontSize: "12px", fontWeight: overdueBy > 0 ? 800 : 600, color: overdueBy > 0 ? T.red : T.ink2 }}
                                        >
                                            {due ? due.toLocaleDateString("en-GB", { day: "2-digit", month: "short" }) : "—"}
                                        </Typography>
                                        <Typography sx={{ fontSize: "12.5px", color: T.ink2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", pr: "8px" }}>
                                            {v.accountName || "—"}
                                        </Typography>
                                        <Typography sx={{ fontSize: "12px", color: T.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", pr: "8px" }}>
                                            {v.subActivity || VISIT_TYPE_LABEL[classify(v.name)]}
                                        </Typography>
                                        <Typography
                                            title={score.points == null ? score.rule : `${score.rule}${v.industry ? ` · ${v.industry}` : ""}`}
                                            sx={{ fontSize: "12.5px", fontWeight: 800, color: score.industryMissing ? T.amber : T.purpleDeep, fontVariantNumeric: "tabular-nums" }}
                                        >
                                            {score.points == null ? "—" : fmtPoints(score.points)}
                                        </Typography>
                                        <Typography sx={{ fontSize: "12px", color: T.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", pr: "8px" }}>
                                            {v.allocatedToName || "unallocated"}
                                        </Typography>
                                        <Typography sx={{ fontSize: "11px", fontWeight: 700, color: v.status === "To be booked" ? T.muted : T.amber }}>
                                            {v.status}
                                        </Typography>
                                    </Box>
                                ))}
                                {inBucket.length > 200 && (
                                    <Typography sx={{ fontSize: "11.5px", color: T.muted2, padding: "8px 10px" }}>
                                        Showing the 200 soonest of {inBucket.length}. Narrow the horizon or team to see the rest —
                                        nothing is hidden by accident.
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    </Panel>
                );
            })}

            {rows.length === 0 && (
                <Box sx={{ p: "28px", color: T.muted, fontSize: "13px" }}>
                    Nothing owed in this window for the current filters.
                </Box>
            )}

            <Typography sx={{ fontSize: "11px", color: T.muted2, lineHeight: 1.6 }}>
                Work with a due date and no diary slot — statuses <b>To be booked</b>, <b>1</b>/<b>2 Attempts</b>,{" "}
                <b>Banked</b> and <b>Suspended</b>. Closed, cancelled and completed visits are excluded, since they
                are not owed.
                <br />
                Points are what the work <b>would</b> score. The On Site / Remote flag is only set once a visit is
                arranged, so most of the pool has none and is priced as an on-site visit — a remote one would score{" "}
                {fmtPoints(config.remotePoints)} instead.
                <br />
                <b>No pairings are proposed.</b> Deciding that two half-day jobs can share a day needs the drive
                time between them, and the specification is explicit that postcode area was only a stand-in for the
                analysis. Half-day work is counted above so the size of the opportunity is visible, but which two
                visits pair waits for real routing.
            </Typography>
        </Box>
    );
};

export default PoolScreen;
